import {
  Book,
  CircleSpark,
  Gift,
  GraduationCap,
  Headset,
  MessageText,
  PageEdit,
  ThumbsUp
} from 'iconoir-react';
import { FC, ReactNode } from 'react';

import { BrandMenuItem } from './BrandMenuItem';
import { SubmenuPanel } from './SubmenuPanel';

import i18n from '~/common/helpers/i18n';

export interface Eula {
  acceptanceDate?: string | null;
  lastPostponementDate?: string | null;
  countDown?: number;
}

export interface HelpSubmenuProps {
  unreadCount?: number;
  eula?: Eula | null;
  enabledSupportAccess?: boolean;
  onChat: () => void;
  onSupportAccess: () => void;
  onLicenseAgreement: () => void;
  onLinkPress: () => void;
}

type HelpMenuItem = {
  testId: string;
  icon: ReactNode;
  label: ReactNode;
  onPress: () => void;
  href?: string;
  endContent?: ReactNode;
  hidden?: boolean;
};

const SEND_FEEDBACK_URL =
  'https://digibee.atlassian.net/servicedesk/customer/portal/6';

export const HelpSubmenu: FC<HelpSubmenuProps> = ({
  unreadCount = 0,
  eula,
  enabledSupportAccess = false,
  onChat,
  onSupportAccess,
  onLicenseAgreement,
  onLinkPress
}) => {
  const items: HelpMenuItem[] = [
    {
      testId: 'help-menu-link-intercom-chat',
      icon: <MessageText className='icon-smallest shrink-0' />,
      label: i18n.t('scenes.beta.actions.open_chat'),
      onPress: onChat,
      endContent:
        unreadCount > 0 ? (
          <span className='whitespace-nowrap rounded-full bg-secondary-container px-2 text-body-regular-sm text-on-secondary-container'>
            {unreadCount}{' '}
            {unreadCount === 1
              ? i18n.t('label.message')
              : i18n.t('label.messages')}
          </span>
        ) : undefined
    },
    {
      testId: 'help-menu-link-intercom-academy',
      icon: <GraduationCap className='icon-smallest shrink-0' />,
      label: i18n.t('label.digibee_academy_link'),
      href: i18n.t('action.digibee_academy_link_url'),
      onPress: onLinkPress
    },
    {
      testId: 'help-menu-link-intercom-documentation',
      icon: <Book className='icon-smallest shrink-0' />,
      label: i18n.t('action.documentation'),
      href: i18n.t('action.documentation_url'),
      onPress: onLinkPress
    },
    {
      testId: 'help-menu-link-ai-assistant-academy',
      icon: <CircleSpark className='icon-smallest shrink-0' />,
      label: i18n.t('action.ai_assistant'),
      href: i18n.t('action.ai_assistant_url'),
      onPress: onLinkPress
    },
    {
      testId: 'help-menu-link-intercom-release-notes',
      icon: <Gift className='icon-smallest shrink-0' />,
      label: i18n.t('scenes.beta.actions.open_news'),
      href: i18n.t('scenes.beta.actions.news_url'),
      onPress: onLinkPress
    },
    {
      testId: 'help-menu-link-intercom-open-send-feedback',
      icon: <ThumbsUp className='icon-smallest shrink-0' />,
      label: i18n.t('scenes.beta.actions.send_feedback'),
      href: SEND_FEEDBACK_URL,
      onPress: onLinkPress
    },
    {
      testId: 'help-menu-link-open-support-access',
      icon: <Headset className='icon-smallest shrink-0' />,
      label: enabledSupportAccess
        ? i18n.t('action.support_access_on')
        : i18n.t('action.support_access_off'),
      onPress: onSupportAccess
    },
    {
      testId: 'help-menu-link-intercom-open-eula1',
      icon: <PageEdit className='icon-smallest shrink-0' />,
      label: i18n.t('scenes.eula.labels.license_term'),
      onPress: onLicenseAgreement,
      hidden: !eula,
      endContent:
        eula && !eula.acceptanceDate && eula.lastPostponementDate ? (
          <span className='whitespace-nowrap rounded-full bg-negative-container px-2 text-body-regular-sm text-on-negative-container'>
            {eula.countDown}
          </span>
        ) : undefined
    }
  ];

  return (
    <SubmenuPanel>
      {items
        .filter(item => !item.hidden)
        .map(item => (
          <BrandMenuItem
            key={item.testId}
            data-testid={item.testId}
            icon={item.icon}
            label={item.label}
            href={item.href}
            onPress={item.onPress}
            endContent={item.endContent}
          />
        ))}
    </SubmenuPanel>
  );
};
